/**
 * CoachPlaybook - Animation Engine
 * Version: 0.6.0
 * Description: Pure utility functions for play animation — Catmull-Rom splines,
 *              path simplification, chevron positioning, ball tracking, and player interpolation.
 *              Ported from BasketballCourt.jsx proven animation system.
 */

// ============================================
// SPORT BALL EMOJI MAPPING
// ============================================

const SPORT_BALL_EMOJI = {
  basketball: '\u{1F3C0}',
  soccer: '\u26BD',
  football: '\u{1F3C8}',
  baseball: '\u26BE',
  volleyball: '\u{1F3D0}',
};

export const getBallEmoji = (sportId) => SPORT_BALL_EMOJI[sportId] || '\u{1F3C0}';

// ============================================
// COLOR UTILITIES
// ============================================

export const hexToRgba = (hex, alpha = 1) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  return `rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)},${alpha})`;
};

// ============================================
// CATMULL-ROM SPLINE — smooth curves through points
// ============================================

export const smoothPath = (points) => {
  if (!points || points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  let d = `M ${points[0].x},${points[0].y}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    const tension = 6;
    const cp1x = p1.x + (p2.x - p0.x) / tension;
    const cp1y = p1.y + (p2.y - p0.y) / tension;
    const cp2x = p2.x - (p3.x - p1.x) / tension;
    const cp2y = p2.y - (p3.y - p1.y) / tension;

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  return d;
};

// ============================================
// PATH SIMPLIFICATION — RDP + Chaikin smoothing
// ============================================

const perpendicularDistance = (point, lineStart, lineEnd) => {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lineLengthSq = dx * dx + dy * dy;

  if (lineLengthSq === 0) {
    return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);
  }

  const t = Math.max(0, Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lineLengthSq));
  const projX = lineStart.x + t * dx;
  const projY = lineStart.y + t * dy;

  return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
};

const rdpSimplify = (pts, epsilon) => {
  if (pts.length < 3) return pts;

  let maxDist = 0;
  let maxIdx = 0;
  const first = pts[0];
  const last = pts[pts.length - 1];

  for (let i = 1; i < pts.length - 1; i++) {
    const dist = perpendicularDistance(pts[i], first, last);
    if (dist > maxDist) {
      maxDist = dist;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = rdpSimplify(pts.slice(0, maxIdx + 1), epsilon);
    const right = rdpSimplify(pts.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
};

export const simplifyPath = (points, minDistance = 3) => {
  if (!points || points.length < 2) return points;

  // Step 1: RDP with tolerance based on path length
  const pathLength = Math.sqrt(
    (points[points.length - 1].x - points[0].x) ** 2 +
    (points[points.length - 1].y - points[0].y) ** 2
  );
  const epsilon = Math.max(1, pathLength * 0.03);
  let simplified = rdpSimplify(points, epsilon);

  // Step 2: Chaikin corner cutting (1 iteration)
  if (simplified.length > 2) {
    const smoothed = [simplified[0]];
    for (let i = 0; i < simplified.length - 1; i++) {
      const p0 = simplified[i];
      const p1 = simplified[i + 1];

      if (i > 0) {
        smoothed.push({
          x: p0.x * 0.75 + p1.x * 0.25,
          y: p0.y * 0.75 + p1.y * 0.25
        });
      }
      if (i < simplified.length - 2) {
        smoothed.push({
          x: p0.x * 0.25 + p1.x * 0.75,
          y: p0.y * 0.25 + p1.y * 0.75
        });
      }
    }
    smoothed.push(simplified[simplified.length - 1]);
    simplified = smoothed;
  }

  // Step 3: Remove points too close together
  const result = [simplified[0]];
  for (let i = 1; i < simplified.length - 1; i++) {
    const last = result[result.length - 1];
    const dist = Math.sqrt((simplified[i].x - last.x) ** 2 + (simplified[i].y - last.y) ** 2);
    if (dist >= minDistance) {
      result.push(simplified[i]);
    }
  }
  result.push(simplified[simplified.length - 1]);

  return result;
};

// ============================================
// PATH LENGTH — Euclidean sum of segments
// ============================================

export const getPathLength = (points) => {
  if (!points || points.length < 2) return 0;
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    length += Math.sqrt(dx * dx + dy * dy);
  }
  return length;
};

// ============================================
// CHEVRON POSITIONS — direction indicators along paths
// ============================================

export const getChevronPositions = (points) => {
  if (!points || points.length < 2) return [];

  const totalLength = getPathLength(points);
  if (totalLength < 8) return [];

  let positions;
  if (totalLength < 20) {
    positions = [0.45, 0.85];
  } else if (totalLength < 40) {
    positions = [0.20, 0.50, 0.85];
  } else {
    positions = [0.15, 0.40, 0.65, 0.90];
  }

  const result = [];

  for (const targetFraction of positions) {
    const targetDist = totalLength * targetFraction;
    let currentDist = 0;

    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;
      const segmentLength = Math.sqrt(dx * dx + dy * dy);

      if (currentDist + segmentLength >= targetDist) {
        const t = (targetDist - currentDist) / segmentLength;
        const x = points[i - 1].x + dx * t;
        const y = points[i - 1].y + dy * t;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        result.push({ x, y, angle });
        break;
      }
      currentDist += segmentLength;
    }
  }

  return result;
};

// ============================================
// POINT ON LINE — linear interpolation
// ============================================

export const getPointOnLine = (from, to, t) => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  angle: Math.atan2(to.y - from.y, to.x - from.x) * (180 / Math.PI)
});

// ============================================
// STEP BADGE POSITION — at 40% along path, offset perpendicular right
// ============================================

export const getStepBadgePosition = (points, fraction = 0.4, offsetDist = 3) => {
  const pathLen = getPathLength(points);
  const targetDist = pathLen * fraction;
  let badgePt = { x: points[0].x, y: points[0].y, angle: 0 };
  let currentDist = 0;

  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    const segLen = Math.sqrt(dx * dx + dy * dy);

    if (currentDist + segLen >= targetDist) {
      const t = (targetDist - currentDist) / segLen;
      badgePt = {
        x: points[i - 1].x + dx * t,
        y: points[i - 1].y + dy * t,
        angle: Math.atan2(dy, dx)
      };
      break;
    }
    currentDist += segLen;
  }

  const perpAngle = badgePt.angle + Math.PI / 2;
  return {
    x: badgePt.x + Math.cos(perpAngle) * offsetDist,
    y: badgePt.y + Math.sin(perpAngle) * offsetDist
  };
};

// ============================================
// PLAYER POSITION AT STEP — endpoint after completed paths
// ============================================

export const getPlayerPositionAtStep = (positionId, atStep, positions, paths) => {
  const pos = positions.find(p => p.id === positionId);
  if (!pos || pos.startX === null) return null;
  let currentX = pos.startX;
  let currentY = pos.startY;
  paths
    .filter(p => p.positionId === positionId && p.stepNumber <= atStep && p.points?.length >= 2)
    .sort((a, b) => a.stepNumber - b.stepNumber)
    .forEach(path => {
      currentX = path.points[path.points.length - 1].x;
      currentY = path.points[path.points.length - 1].y;
    });
  return { x: currentX, y: currentY };
};

// ============================================
// ANIMATED POSITION — per-frame player position with speed support
// ============================================

export const getAnimatedPosition = (pos, currentStep, isAnimating, animationProgress, positions, paths) => {
  if (!pos || pos.startX === null) return null;

  // Step 0 always shows starting positions
  if (currentStep === 0) {
    return { x: pos.startX, y: pos.startY };
  }

  // Design mode: show position at end of completed paths
  if (!isAnimating) {
    return getPlayerPositionAtStep(pos.id, currentStep, positions, paths) || { x: pos.startX, y: pos.startY };
  }

  // Animation mode: interpolate along current step's path
  const playerSpeed = pos.speed || 1.0;
  const adjustedProgress = Math.min(1, animationProgress * playerSpeed);

  const basePos = getPlayerPositionAtStep(pos.id, currentStep - 1, positions, paths) || { x: pos.startX, y: pos.startY };
  const currentStepPath = paths.find(p => p.positionId === pos.id && p.stepNumber === currentStep && p.points?.length >= 2);

  if (!currentStepPath) {
    if (playerSpeed > 1 && adjustedProgress >= 1) {
      return getPlayerPositionAtStep(pos.id, currentStep, positions, paths) || basePos;
    }
    return getPlayerPositionAtStep(pos.id, currentStep, positions, paths) || basePos;
  }

  const pts = currentStepPath.points;
  const idx = adjustedProgress * (pts.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.min(lo + 1, pts.length - 1);
  const t = idx - lo;
  return {
    x: pts[lo].x + (pts[hi].x - pts[lo].x) * t,
    y: pts[lo].y + (pts[hi].y - pts[lo].y) * t
  };
};

// ============================================
// BALL HOLDER TRACKING — who has the ball at a given step
// ============================================

export const getBallHolderAtStep = (atStep, positions, paths, passes) => {
  let ballHolder = positions.find(p => p.hasBall)?.id || positions[0]?.id;
  const events = [];
  paths.filter(p => p.type === 'dribble' && p.stepNumber <= atStep).forEach(p => events.push({ step: p.stepNumber, holder: p.positionId }));
  passes.filter(p => p.stepNumber <= atStep).forEach(p => events.push({ step: p.stepNumber, holder: p.toPositionId }));
  events.sort((a, b) => a.step - b.step).forEach(e => { ballHolder = e.holder; });
  return ballHolder;
};

// ============================================
// DEFENDER POSITION — interpolation along explicit paths
// ============================================

export const getDefenderPosition = (def, defenderPaths, currentStep, isAnimating, animationProgress) => {
  let defX = def.x;
  let defY = def.y;

  const defPathsForThis = defenderPaths.filter(p => p.defenderId === def.id);

  if (defPathsForThis.length === 0) {
    return { x: defX, y: defY };
  }

  let currentDefX = def.x;
  let currentDefY = def.y;

  if (!isAnimating) {
    // Design mode: use end position of paths up to current step
    defPathsForThis
      .filter(p => (p.stepNumber || 1) <= currentStep)
      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1))
      .forEach(path => {
        if (path.points?.length >= 2) {
          const lastPoint = path.points[path.points.length - 1];
          currentDefX = lastPoint.x;
          currentDefY = lastPoint.y;
        }
      });
    return { x: currentDefX, y: currentDefY };
  }

  // Animation mode: interpolate along current path
  const defenderSpeed = def.speed || 1.0;
  const defAdjustedProgress = Math.min(1, animationProgress * defenderSpeed);

  defPathsForThis
    .filter(p => (p.stepNumber || 1) < currentStep)
    .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1))
    .forEach(path => {
      if (path.points?.length >= 2) {
        const lastPoint = path.points[path.points.length - 1];
        currentDefX = lastPoint.x;
        currentDefY = lastPoint.y;
      }
    });

  const currentDefPath = defPathsForThis.find(p => (p.stepNumber || 1) === currentStep);
  if (currentDefPath && currentDefPath.points?.length >= 2) {
    const pts = currentDefPath.points;
    const idx = defAdjustedProgress * (pts.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, pts.length - 1);
    const t = idx - lo;
    return {
      x: pts[lo].x + (pts[hi].x - pts[lo].x) * t,
      y: pts[lo].y + (pts[hi].y - pts[lo].y) * t
    };
  }

  return { x: currentDefX, y: currentDefY };
};

// ============================================
// PASS BALL POSITION — interpolation for animated ball on passes
// ============================================

export const getPassBallPosition = (fromPos, toPos, progress, passType) => {
  if (passType === 'lob') {
    const controlX = (fromPos.x + toPos.x) / 2;
    const controlY = Math.min(fromPos.y, toPos.y) - 8;
    const t = progress;
    return {
      x: (1 - t) * (1 - t) * fromPos.x + 2 * (1 - t) * t * controlX + t * t * toPos.x,
      y: (1 - t) * (1 - t) * fromPos.y + 2 * (1 - t) * t * controlY + t * t * toPos.y
    };
  }
  return {
    x: fromPos.x + (toPos.x - fromPos.x) * progress,
    y: fromPos.y + (toPos.y - fromPos.y) * progress
  };
};

// ============================================
// SHOT BALL POSITION — quadratic bezier to hoop/goal
// ============================================

export const getShotBallPosition = (shooterPos, targetPos, progress) => {
  const arcHeight = Math.abs(shooterPos.y - targetPos.y) * 0.6 + 8;
  const controlX = (shooterPos.x + targetPos.x) / 2;
  const controlY = Math.min(shooterPos.y, targetPos.y) - arcHeight;
  const t = progress;
  return {
    x: (1 - t) * (1 - t) * shooterPos.x + 2 * (1 - t) * t * controlX + t * t * targetPos.x,
    y: (1 - t) * (1 - t) * shooterPos.y + 2 * (1 - t) * t * controlY + t * t * targetPos.y,
    scale: 1 - (progress * 0.3)
  };
};

// ============================================
// GHOST CIRCLE OFFSET — offset path start from ghost circle edge
// ============================================

export const getOffsetStartPoint = (points, ghostRadius = 3) => {
  if (!points || points.length < 2) return points?.[0] || { x: 0, y: 0 };
  const startPoint = points[0];
  const nextPoint = points[1];
  const dx = nextPoint.x - startPoint.x;
  const dy = nextPoint.y - startPoint.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist > 0) {
    return {
      x: startPoint.x + (dx / dist) * ghostRadius,
      y: startPoint.y + (dy / dist) * ghostRadius
    };
  }
  return startPoint;
};

// ============================================
// CSS ANIMATION KEYFRAMES — injected into DOM for chevron/ball-holder effects
// ============================================

export const ANIMATION_STYLES = `
  @keyframes ballHolderGlow {
    0%, 100% { filter: drop-shadow(0 0 2px rgba(255,165,0,0.8)); }
    50% { filter: drop-shadow(0 0 5px rgba(255,165,0,1)); }
  }

  @keyframes fillChevron1of4 {
    0%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron2of4 {
    0%, 16.6% { opacity: 0.2; }
    16.7%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron3of4 {
    0%, 33.2% { opacity: 0.2; }
    33.3%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron4of4 {
    0%, 49.9% { opacity: 0.2; }
    50%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }

  @keyframes fillChevron1of3 {
    0%, 90% { opacity: 0.9; }
    90.1%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron2of3 {
    0%, 19.9% { opacity: 0.2; }
    20%, 90% { opacity: 0.9; }
    90.1%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron3of3 {
    0%, 39.9% { opacity: 0.2; }
    40%, 90% { opacity: 0.9; }
    90.1%, 100% { opacity: 0.2; }
  }

  @keyframes fillChevron1of2 {
    0%, 85% { opacity: 0.9; }
    85.1%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron2of2 {
    0%, 29.9% { opacity: 0.2; }
    30%, 85% { opacity: 0.9; }
    85.1%, 100% { opacity: 0.2; }
  }

  @keyframes fillChevron1of1 {
    0%, 60% { opacity: 0.9; }
    60.1%, 100% { opacity: 0.2; }
  }

  .ball-holder { animation: ballHolderGlow 1.5s ease-in-out infinite; }

  .chevron-1-of-4 { animation: fillChevron1of4 2.4s ease-in-out infinite; }
  .chevron-2-of-4 { animation: fillChevron2of4 2.4s ease-in-out infinite; }
  .chevron-3-of-4 { animation: fillChevron3of4 2.4s ease-in-out infinite; }
  .chevron-4-of-4 { animation: fillChevron4of4 2.4s ease-in-out infinite; }

  .chevron-1-of-3 { animation: fillChevron1of3 2.0s ease-in-out infinite; }
  .chevron-2-of-3 { animation: fillChevron2of3 2.0s ease-in-out infinite; }
  .chevron-3-of-3 { animation: fillChevron3of3 2.0s ease-in-out infinite; }

  .chevron-1-of-2 { animation: fillChevron1of2 1.8s ease-in-out infinite; }
  .chevron-2-of-2 { animation: fillChevron2of2 1.8s ease-in-out infinite; }

  .chevron-1-of-1 { animation: fillChevron1of1 1.6s ease-in-out infinite; }
`;

// ============================================
// SHOT TARGET POSITIONS — sport-specific goal/hoop locations
// ============================================

export const SHOT_TARGETS = {
  basketball: { x: 50, y: 10.5 },
  soccer: { x: 50, y: 0 },
  football: { x: 50, y: 0 },
  volleyball: { x: 50, y: 0 },
  baseball: { x: 50, y: 50 },
};

export const getShotTarget = (sportId) => SHOT_TARGETS[sportId] || SHOT_TARGETS.basketball;
